const Location = require('utils/Location');
const Storage = require('utils/localStorage/Storage');
const Flush = require('utils/localStorage/Flush');
const ErrorConstants = require('utils/ErrorConstants');
const LOCAL_STORAGE = require('utils/localStorage/Constants');
const basketUtils = require('utils/Basket');

const DISABLED_GUEST_CHECKOUT_TT_OFFER = 'DisableGuestCheckout';
let requestCounter = 0;

const ResponseErrorCode = {

    NETWORK_REQUEST_FAILED: -666666,
    REQUEST_ABORTED: -777777,

    // The token's lifetime is over and the user needs to do the manual
    // re-login with SSI.
    SSI_TOKEN_EXPIRED: 407,

    // The SSI user needs to perform the auto-login.
    SSI_SIGN_IN_REQUIRED: 409,

    //ATG 11 Migration error code, BE moves user info from atg9 to atg11, redo api call.
    ATG_11_MIGRATION: 347,

    ORDER_IS_NOT_INITIALIZED: 422
};

const SUBMIT_ORDER_URL = '/api/checkout/submitOrder';
const AUTO_LOGIN_URL = 'api/ssi/autoLogin';


const ResponseStatusCode = {

    // Per conversation with Lijo Jacob, any 403 Forbidden HTTP response
    // means nothing but that a request is forbidden for auto-login users,
    // and so user needs to perform a manual sign-in.
    FORBIDDEN: 403
};

const NUM_RETRIES = 3;
const RETRY_TIMEOUT_MS = 10000;

function getCallsCounter() {
    return requestCounter;
}
function flushCache() {
    Flush.flushUser();
    Flush.flushBasket();
}

function performManualSignInAndMakeRequest(originalUrl, originalOptions, errorMessages) {
    const store = require('Store');
    const UserActions = require('actions/UserActions');
    const UserUtils = require('utils/User');
    const BasketActions = require('actions/BasketActions');

    const ORDER_INIT_API = '/api/checkout/order/init';

    let user = store.getState().user;
    let isRopisBasket = () => Storage.local.getItem(LOCAL_STORAGE.BASKET_TYPE) === BasketActions.BASKET_TYPES.ROPIS_BASKET;
    let isBasketInitCheckout =
        originalUrl.indexOf(ORDER_INIT_API) >= 0 && Location.isBasketPage() && !isRopisBasket();

    let showGuestCheckoutModal = store.getState().basket.isGuestCheckoutEnabled && !UserUtils.isSignedIn();

    // Make user recognized, if the store thinks that she's still signed in.
    if (UserUtils.isSignedIn()) {
        user.profileStatus = 2;
        store.dispatch(UserActions.update(user));
    }

    let attemptToSignIn = new Promise((resolve, reject) => {
        const IntersticeRequestActions = require('actions/IntersticeRequestActions');
        const Actions = require('Actions');

        store.dispatch(IntersticeRequestActions.clear());
        store.dispatch(Actions.showInterstice(false));

        let preventRegistrationModal = isBasketInitCheckout;

        let options = originalOptions.body ? JSON.parse(originalOptions.body) : {};

        const cachedTargetReferrer = Storage.local.getItem(LOCAL_STORAGE.TARGET_REFERRER);
        const isEmailTraffic = cachedTargetReferrer !== null ?
            cachedTargetReferrer.indexOf('ret') === 0 || cachedTargetReferrer.indexOf('tr') === 0 :
            false;

        if (showGuestCheckoutModal && !options.RopisCheckout && !isEmailTraffic) {
            store.dispatch(Actions.showSignInWithMessagingModal({
                isOpen: true,
                messages: errorMessages,
                callback: resolve,
                isApplePayFlow: options.isApplePayFlow,
                isPaypalFlow: options.isPaypalFlow,
                analyticsData: {},
                errback: reject
            }));
        } else {
            store.dispatch(Actions.showSignInModal({
                isOpen: true,
                messages: errorMessages,
                callback: resolve,
                isNewUserFlow: preventRegistrationModal,
                isApplePayFlow: options.isApplePayFlow,
                RopisCheckout: options.RopisCheckout,
                analyticsData: {},
                errback: reject
            }));
        }
    });

    return attemptToSignIn.then(userData => {
        return new Promise((resolve, reject) => {

            // require.ensure is used to package nested requires in components.chunk.js
            // rather than priority.bundle.js in order to keep priority.bundle.js lean
            require.ensure([], function (require) {
                const checkoutUtils = require('utils/Checkout');
                let options = originalOptions && originalOptions.body ?
                    JSON.parse(originalOptions.body) : {};
                let promise;
                if (isBasketInitCheckout) {
                    // Try to init order after interrrupt by sign in
                    promise = checkoutUtils.initializeCheckout({
                        isPaypalFlow: options.isPaypalFlow,
                        isApplePayFlow: options.isApplePayFlow,
                        user: userData,
                        signInResponse: userData,
                        ropisCheckout: basketUtils.isPickup()
                    });
                } else {
                    // eslint-disable-next-line no-use-before-define
                    promise = makeRequest(originalUrl, originalOptions);
                }

                resolve(promise);
            }, 'components');
        });
    }).catch(response => {
        return response;
    });
}

function performAutoLoginAndMakeRequest(originalUrl, originalOptions) {
    const store = require('Store');
    const Actions = require('Actions');
    const ssiApi = require('services/api/ssi');
    store.dispatch(Actions.showInterstice(false));

    return new Promise((resolve, reject) => {
        require.ensure([], function (require) {
            const { setupFingerprint } = require('services/Fingerprint');
            setupFingerprint((deviceFingerprint) => {
                const newOptions = { ...originalOptions, deviceFingerprint };
                resolve(ssiApi.autoLogin({ deviceFingerprint })
                    .then(() => {
                        // eslint-disable-next-line no-use-before-define
                        return makeRequest(originalUrl, newOptions);
                    })
                    .catch(reason => performManualSignInAndMakeRequest(
                        originalUrl, originalOptions, reason.errorMessages)));
            });
        });
    });
}

function handleResponse(responseStatus, responseData, originalUrl, originalOptions) {
    let promise;

    // Per conversation with Alexey Filonov, errorCode should always be
    // present, defaulting to -1.
    //
    // No specific HTTP response status is enforced to have errorCode, so it
    // can be present in response with any status.
    // That's likely because of various BE engineers in various BE teams
    // having interpreted some best practice in the past differenly.
    //
    // So, first of all, we handle general error codes for SSI expiration
    // and auto-login. Other error codes are considered endpoint specific,
    // so don't handle them here. Finally, 403 (like on login) is handled.


    if (responseData.errorCode === ResponseErrorCode.ATG_11_MIGRATION) {
        // eslint-disable-next-line no-use-before-define
        promise = makeRequest(originalUrl, originalOptions);

    } else if (responseData.errorCode === ResponseErrorCode.SSI_TOKEN_EXPIRED) {
        promise = performManualSignInAndMakeRequest(
            originalUrl, originalOptions, responseData.errorMessages);

    } else if (responseData.errorCode === ResponseErrorCode.SSI_SIGN_IN_REQUIRED) {
        promise = performAutoLoginAndMakeRequest(originalUrl, originalOptions);
    } else if (responseData.errorCode === ResponseErrorCode.ORDER_IS_NOT_INITIALIZED &&
        Location.isPlayEdit()) {
        Location.setLocation('/basket');
    } else if (responseData.errorCode) {
        promise = Promise.reject(responseData);

    } else if (responseStatus === ResponseStatusCode.FORBIDDEN) {
        if (originalUrl.indexOf(AUTO_LOGIN_URL) !== -1) {
            promise = Promise.reject(responseData);
        } else {
            promise = performManualSignInAndMakeRequest(originalUrl, originalOptions);
        }
    } else {
        promise = Promise.resolve(
            Object.assign({}, responseData, {
                responseStatus
            }));
    }

    return promise;
}


function buildRequestUrl(pathWithQueryString) {
    // Per conversation with Lijo Jacob, we're safe to always shoot HTTPS requests.
    if (pathWithQueryString.indexOf('https://') === 0) {
        return pathWithQueryString;
    }

    let host = Sephora.host || window.location.hostname;
    let portPart = (Sephora.sslPort === undefined ||
        Sephora.sslPort === 443 || Sephora.sslPort === '') ?
        '' : ':' + Sephora.sslPort;

    return `https://${host}${portPart}${pathWithQueryString}`;
}

function makeSingleRequest(url, options = {}, config = {}) {
    requestCounter++;
    // Create an instance of AbortController and include its 'signal' property to the request options
    // AbortController instance will be passed back as config.abortController
    // Later on the request can be cncelled by calling config.abortController.abort()
    if (config.abortable && window.AbortController) {
        const controller = new window.AbortController();
        options.signal = controller.signal;
        config.abortController = controller;
    }

    const fetchOptions = {
        credentials: 'include',
        importance: 'high',
        ...options
    };

    return window.fetch(url, fetchOptions)
        .then(response => {
            return response.json().then(responseData => {
                if (config.returnHeaders) {
                    responseData['headers'] = response.headers;
                }
                // eslint-disable-next-line no-use-before-define
                return decorateResponseWithCheckoutLogic(handleResponse)(
                    response.status, responseData, url, options);
            });
        });
}

function checkoutSignInAndMakeRequest(originalUrl, originalOptions, errorMessages) {
    const store = require('Store');

    let user = store.getState().user;
    let basket = store.getState().basket;

    const UserActions = require('actions/UserActions');
    const UserUtils = require('utils/User');
    const OrderUtils = require('utils/Order');

    let checkoutRedirectUrl;
    if (OrderUtils.isPlayEditOrder()) {
        checkoutRedirectUrl = '/profile/MyAccount/Subscriptions';
    } else if (OrderUtils.isPlayOrder()) {
        checkoutRedirectUrl = '/product/play-subscription-P396286';
    } else {
        checkoutRedirectUrl = '/basket';
    }
    if (UserUtils.isAnonymous()) {
        Location.setLocation(checkoutRedirectUrl);
        return Promise.resolve();
    }
    // Make user recognized, if the store thinks that she's still signed in.
    if (UserUtils.isSignedIn()) {
        let updatedUser = Object.assign({}, user, {
            profileStatus: 2
        });
        store.dispatch(UserActions.update(updatedUser));
    }

    let attemptToSignIn = new Promise((resolve, reject) => {
        const IntersticeRequestActions = require('actions/IntersticeRequestActions');
        const Actions = require('Actions');

        store.dispatch(IntersticeRequestActions.clear());
        store.dispatch(Actions.showInterstice(false));

        store.dispatch(Actions.showSignInModal({
            isOpen: true,
            messages: errorMessages,
            callback: resolve,
            isNewUserFlow: true,
            analyticsData: {},
            errback: reject
        }));
    });

    return attemptToSignIn.then(userData => {
        return new Promise((resolve, reject) => {

            // require.ensure is used to package nested requires in components.chunk.js
            // rather than priority.bundle.js in order to keep priority.bundle.js lean
            require.ensure([], function (require) {
                const checkoutUtils = require('utils/Checkout');
                let promise;

                //if, for whatever reason, another user is signing in, then redirect to correct url
                let redirectFromCheckout = user.profileId !== UserUtils.getProfileId();
                // or in Play became recognized
                redirectFromCheckout = redirectFromCheckout || OrderUtils.isPlayEditOrder() ||
                    OrderUtils.isPlayOrder();
                if (redirectFromCheckout) {
                    flushCache();
                    Location.setLocation(checkoutRedirectUrl);
                    promise = Promise.resolve();
                } else {
                    // if the user becomes recognized on the checkout page
                    // checkout needs to be reinitialized with the user's profile id
                    promise = checkoutUtils.reinitializeOrder(userData.profileId).then(() => {
                        if (originalUrl.indexOf(SUBMIT_ORDER_URL) !== -1) {
                            // Do not Submit Order silently, after relogin.
                            // Let user review the Order before submitting
                            Location.reload();
                            return Promise.resolve();
                        } else {
                            // eslint-disable-next-line no-use-before-define
                            return makeRequest(originalUrl, originalOptions);
                        }
                    });
                }

                resolve(promise);
            }, 'components');
        });
    }).catch(response => {
        flushCache();
        Location.setLocation(checkoutRedirectUrl);
        return response;
    });
}

function checkoutAutoLoginAndMakeRequest(originalUrl, originalOptions) {
    const store = require('Store');
    const Actions = require('Actions');
    const ssiApi = require('services/api/ssi');
    store.dispatch(Actions.showInterstice(false));

    return new Promise((resolve, reject) => {
        require.ensure([], function (require) {
            const { setupFingerprint } = require('services/Fingerprint');

            setupFingerprint((deviceFingerprint) => {
                resolve(ssiApi.autoLogin({ deviceFingerprint })
                    .then((userData) => {
                        return new Promise((_resolve, _reject) => {
                            const checkoutUtils = require('utils/Checkout');
                            let promise = checkoutUtils.reinitializeOrder(userData.profileId);
                            _resolve(promise.then(() => {
                                // eslint-disable-next-line no-use-before-define
                                return makeRequest(originalUrl, originalOptions);
                            }));
                        });
                    })
                    .catch(reason => checkoutSignInAndMakeRequest(
                        originalUrl, originalOptions, reason.errorMessages))
                );
            });
        });
    });
}

function decorateResponseWithCheckoutLogic(decorated) {
    return function (...args) {
        if (Location.isCheckout() || Location.isPlayEdit()) {
            let [
                responseStatus,
                responseData,
                originalUrl,
                originalOptions
            ] = args;

            if (responseData.errorCode === ResponseErrorCode.SSI_TOKEN_EXPIRED) {
                return checkoutSignInAndMakeRequest(
                    originalUrl, originalOptions, responseData.errorMessages);

            } else if (responseData.errorCode === ResponseErrorCode.SSI_SIGN_IN_REQUIRED) {
                return checkoutAutoLoginAndMakeRequest(originalUrl, originalOptions);
            } else if (responseData.errorCode === ResponseErrorCode.ORDER_IS_NOT_INITIALIZED) {
                Location.setLocation('/basket');
                return decorated(...args);
            } else if (!responseData.errorCode && responseStatus === ResponseStatusCode.FORBIDDEN) {
                return checkoutSignInAndMakeRequest(originalUrl, originalOptions);
            } else {
                return decorated(...args);
            }

        } else {
            return decorated(...args);
        }
    };
}

function decorateRequestMakerWithRetryLogic(decorated) {
    return function (...args) {
        const lastArg = args[args.length - 1];
        const config = typeof lastArg === 'object' ? lastArg : {};
        let numRetries = config.numRetries || NUM_RETRIES;
        let retryTimeout = config.retryTimeout || RETRY_TIMEOUT_MS;

        function _makeRequest(...args2) {
            return decorated(...args2).catch(reason => {
                let promise;

                if (reason instanceof TypeError &&
                    reason.message === 'Network request failed') {

                    if (numRetries > 0) {
                        numRetries = numRetries - 1;

                        promise = new Promise((resolve2, reject2) => {
                            setTimeout(function () {
                                _makeRequest(...args2).then(resolve2).catch(reject2);
                            }, retryTimeout);
                        });

                    } else {
                        promise = Promise.reject({
                            errorCode: ResponseErrorCode.NETWORK_REQUEST_FAILED
                        });
                    }

                } else {
                    promise = Promise.reject(reason);
                }

                return promise;
            });
        }

        return _makeRequest(...args);
    };
}

// This will be called as
// abortable(promise, control)(promiseArgument1, promiseArgument2...)
function decorateRequestMakerWithAbortableLogic(decorated, control) {
    return function (...args) {
        let abort;

        let promise = new Promise((resolve, reject) => {
            abort = () => reject({
                errorCode: ResponseErrorCode.REQUEST_ABORTED
            });

            decorated(...args).then(resolve).catch(reject);
        });

        control.abort = abort;

        return promise;
    };
}


let makeRetryingRequest = decorateRequestMakerWithRetryLogic(makeSingleRequest);

// The last parameter config is an optional object which can take the following arguments:
// numRetries - will override the constant NUM_RETRIES
// retryTimeout - will override the constant RETRY_TIMEOUT_MS
// abortable - allows to abort the fetch request
// returnHeaders - allows the service to return the headers as part of the response object
function makeRequest(pathWithQueryString, options, config = {}) {
    let url = buildRequestUrl(pathWithQueryString);
    return makeRetryingRequest(url, options, config);
}

module.exports = {
    ResponseErrorCode,
    ResponseStatusCode,
    makeRequest,
    abortable: decorateRequestMakerWithAbortableLogic,
    performManualSignInAndMakeRequest,
    handleResponse,
    getCallsCounter,
    makeSingleRequest
};
